import { useEffect, useState } from "react"
import DataTable from "react-data-table-component"
import { Card, Spinner, Alert, Badge, Tabs, Tab } from "react-bootstrap"
import useFetch from "../../../_fetch"
import * as DateFns from 'date-fns'
import { formatNumber, getColor,formatNumberLocale } from "../../../_utils"
import { useAuth } from "../../../auth/auth-context"
import CustomAlerts from "./custom-alerts/custom-alerts"

export const comparisonOptions = [
  { label: "Previous 3 days", value: "3d" },
  { label: "Previous 7 days", value: "7d" },
  { label: "Previous 14 days", value: "14d" },
  { label: "Previous 30 days", value: "30d" },
]

export type IConversion = {
    id: string
    commission: number
    date: string
    status: string
    country: string
    network: string
    ref: string
    credentialsId: string
    campaignId: string
    campaignName: string
    affiliateId: string
    affiliateName: string
    orderAmount: number
    orderRef: string
}

export type IFeed_Alert = {
    date: string,
    error: string,
    feedName: string,
    linkName: string,
    feedId: string,
    linkId: string
}

export type ICustomAlert = {
    id: string,
    name: string,
    type: string,
    direction: "increase" | "decrease",
    percentage: number,
    comparisonPeriod: string,
    metric: "revenue" | "commission" | "clicks",
    period1Start: string,
    period1End: string,
    period2Start: string,
    period2End: string,
    period1Value: number,
    period2Value: number,
    date: string
    disabled?: boolean
}

export function getAlertMetricLabel(metric: ICustomAlert["metric"]) {
    if (metric === "revenue") return "Revenue"
    if (metric === "commission") return "Commission"
    return "Clicks"
}

export type IDuplicateConversions = {
    [orderRef: string]: IConversion[]
}

function getColumns(currency: string) {
    const columns = [
        {
            name: "Type",
            selector: (row: any) => row.type,
            sortable: true,
            width: '200px',
        },
        {
            name: "Date",
            selector: (row: any) => row.date,
            sortable: true,
            width: '200px',
            id: 'date'
        },
        {
            name: "Details",
            selector: (row: any) => row.details,
            sortable: false,
            wrap: true,
            format: (row: any) => {
                if (row.type === 'Custom Alert') {
                    const alert: ICustomAlert = row.details
                    const isIncrease = alert.direction === 'increase'
                    const dirText = isIncrease ? 'Gain' : 'Drop'
                    const dirColor = isIncrease ? 'success' : 'danger'
                    const metricLabel = getAlertMetricLabel(alert.metric)
                    const percentage = `${isIncrease ? '+' : ''}${formatNumberLocale(alert.percentage, 1)}%`
                    const comparisonPeriod = comparisonOptions.find(opt => opt.value === alert.comparisonPeriod)?.label || alert.comparisonPeriod

                    const formattedP1 = alert.metric === 'clicks' ? formatNumberLocale(alert.period1Value, 0) : formatNumber(alert.period1Value, currency)
                    const formattedP2 = alert.metric === 'clicks' ? formatNumberLocale(alert.period2Value, 0) : formatNumber(alert.period2Value, currency)

                    return (
                        <div className="d-flex align-items-start">
                            <div className="my-auto" style={{ width: '120px' }}>
                                <strong>{metricLabel} {dirText}</strong>
                            </div>
                            <div>
                                <div>
                                    <div><strong>Alert name:</strong> {alert.name}</div>
                                    <div>
                                        <strong>Change:</strong> {comparisonPeriod} {dirText.toLowerCase()} by{' '}
                                        <span className={`text-${dirColor}`}><strong>{percentage}</strong></span>
                                        <span className="ms-2"><small>({formattedP2} → {formattedP1})</small></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }



                if (row.type === 'Feeds') {
                    return (
                        <div className="d-flex align-items-center">
                            <div className="text-danger d-flex align-items-center" style={{ width: '120px' }}>
                                <Badge pill bg="danger">Update Error</Badge>
                            </div>

                            <div>
                                <div><strong>Feed Name:</strong> {row.details.feedName} → {row.details.linkName}</div>
                                <div><strong>Error:</strong> <i>{row.details.error}</i></div>
                            </div>
                        </div>
                    )
                }

                return row.details.map((conversion: any, index: number) => {
                    const networkColor = getColor(conversion.network, 0)
                    const orderAmount = formatNumber(conversion.orderAmount, currency)

                    const flag = renderFlag(conversion.country)
                    const badgeColor = conversion.status === 'approved' ? 'success' : conversion.status === 'rejected' ? 'danger' : 'secondary'
                    const badgeName = conversion.status === 'approved' ? 'Approved' : conversion.status === 'rejected' ? 'Rejected' : 'Pending'

                    return (
                        <div className="d-flex mb-1" key={index}>
                            <div style={{ width: '120px' }}>
                                {flag && <img src={`/flags/${conversion.country.toLowerCase()}.svg`} alt={conversion.country} style={{ height: '15px', marginRight: '5px' }} />}
                                <strong style={{ color: networkColor }}>{conversion.network}</strong>
                            </div>
                            <div>{conversion.orderRef} - </div>
                            <strong className="ms-1">{orderAmount}</strong>
                            <span className="ms-1">- {conversion.campaignName}</span>
                            <span className="ms-1">- {conversion.affiliateName}</span>
                            <Badge pill bg={badgeColor} className="ms-2">
                                {badgeName}
                            </Badge>
                        </div>
                    )
                })
            }
        },
    ]
    return columns
}

function renderFlag(country: string) {
    if (!country || country.toUpperCase() === 'UNKNOWN') return null
    const flagUrl = `/flags/${country.toLowerCase()}.svg`
    return <img className='ms-1 border' src={flagUrl} alt={country} style={{ height: '10px' }} />
}

export default function Alerts() {
    const { doFetch } = useFetch()
    const { user } = useAuth()
    
    const currency = user?.settings?.currency || 'EUR'
    const [data, setData] = useState<{ duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] }>({ duplicateOrderRefs: {}, feeds: [], customAlerts: [] })
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await doFetch("/alerts/get-alerts")
                const json: { duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] } = await response.json()
                setData(json)
            } catch (err) {
                setError("Failed to load alerts.")
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const formatData = () => {
        const rows = []

        for (const orderRef in data.duplicateOrderRefs) {
            if(orderRef === '') continue
            
            if (data.duplicateOrderRefs.hasOwnProperty(orderRef)) {
                const conversions = data.duplicateOrderRefs[orderRef]
                const sortedConversions = [...conversions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).reverse()
                
                const newest = sortedConversions[0]

                if (newest) {
                    rows.push({
                        type: "Duplicate conversion",
                        date: DateFns.format(new Date(newest.date), 'yyyy-MM-dd HH:mm:ss'),
                        details: data.duplicateOrderRefs[orderRef].slice(0, 5),
                    })
                }
            }
        }

        for (const feed of data.feeds) {
            rows.push({
                type: 'Feeds',
                date: DateFns.format(new Date(feed.date), 'yyyy-MM-dd HH:mm:ss'),
                details: feed,
            })
        }

        for(const alert of data.customAlerts) {
            rows.push({ type: 'Custom Alert', date: alert.date, details: alert })
        }

        return rows
    }

    return (
        <Card className="p-3">
            <h3 className="mt-3">Alerts</h3>

            <Tabs defaultActiveKey="alerts" className="mt-3">
                <Tab eventKey="alerts" title="Alert log">
                    {loading ? (
                        <Spinner animation="border" />
                    ) : error ? (
                        <Alert variant="danger" className="mt-3">{error}</Alert>
                    ) : (
                        <DataTable
                            columns={getColumns(currency)}
                            data={formatData()}
                            pagination
                            highlightOnHover
                            defaultSortFieldId={"date"}
                            defaultSortAsc={false}
                            className="mt-3"
                        />
                    )}
                </Tab>

                <Tab eventKey="custom" title="Manage alerts">
                        <CustomAlerts />
                </Tab>
            </Tabs>
        </Card>
    )
}



/*
Alerts to do
- Check hvis API stopper for et netværk
*/
